import { FormOnSubmitEditor } from "@/components/FormOnSubmitEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import type { PageOnSubmitActionType } from "@/types/pageFormSubmit";

interface SettingsTabProps {
  title: string;
  route: string;
  onTitleChange: (title: string) => void;
  onRouteChange: (route: string) => void;
  pageType?: "html" | "markdown" | "redirect";
  onPageTypeChange?: (type: "html" | "markdown" | "redirect") => void;
  redirectUrl?: string;
  onRedirectUrlChange?: (url: string) => void;
  formOnSubmit?: Record<string, PageOnSubmitActionType[]>;
  onFormOnSubmitChange?: (formOnSubmit: Record<string, PageOnSubmitActionType[]>) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
}

export function SettingsTab({
  title,
  route,
  onTitleChange,
  onRouteChange,
  pageType = "html",
  onPageTypeChange,
  redirectUrl,
  onRedirectUrlChange,
  formOnSubmit,
  onFormOnSubmitChange,
  onSave,
  isSaving,
  hasUnsavedChanges,
}: SettingsTabProps) {
  return (
    <div className="flex-1 overflow-auto p-4 space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="page-name">Page name</Label>
          <Input
            id="page-name"
            type="text"
            placeholder="Page name"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="page-route">Route</Label>
          <Input
            id="page-route"
            type="text"
            placeholder="/route"
            value={route}
            onChange={(e) => onRouteChange(e.target.value)}
            className="font-mono"
          />
        </div>
        {onPageTypeChange && (
          <div className="space-y-2">
            <Label htmlFor="page-type">Content type</Label>
            <Select value={pageType} onValueChange={(v) => onPageTypeChange(v as "html" | "markdown" | "redirect")}>
              <SelectTrigger id="page-type" className="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="html">HTML</SelectItem>
                <SelectItem value="markdown">Markdown</SelectItem>
                <SelectItem value="redirect">Redirect</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {pageType === "redirect" && onRedirectUrlChange && (
          <div className="space-y-2">
            <Label htmlFor="redirect-url">Target URL</Label>
            <Input
              id="redirect-url"
              type="url"
              placeholder="https://example.com"
              value={redirectUrl ?? ""}
              onChange={(e) => onRedirectUrlChange(e.target.value)}
            />
          </div>
        )}
        {onSave && (
          <Button
            type="button"
            onClick={() => onSave()}
            disabled={isSaving || !hasUnsavedChanges}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        )}
      </div>

      {pageType !== "redirect" && formOnSubmit !== undefined && onFormOnSubmitChange ? (
        <FormOnSubmitEditor formOnSubmit={formOnSubmit} onChange={onFormOnSubmitChange} />
      ) : pageType !== "redirect" ? (
        <p className="text-sm text-gray-500">Form submit actions not available.</p>
      ) : null}
    </div>
  );
}
